import React, { PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { useInView } from 'react-intersection-observer';
import clsx from 'clsx';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { MDSLeftNavBar, MDSMarqContentWhiteLogo, MDSNavItem, MDSTheme } from '@marqvision/mds/core';
import { ToastContainer } from '@marqvision/shared';
import MonitoringContextProvider from '~/pages/Enforcement/MonitoringContext';
import { RouteParams } from './RouteParams';
import { LayoutWrapperContext } from './LayoutWrapper';
import Header, { headerHeight } from './Header/Header';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      width: '100%',
      overflow: 'overlay',
      overflowX: 'hidden',
      marginTop: headerHeight,
      marginLeft: '16px',
      '& #sticky-header': {
        backgroundColor: MDSTheme.palette.white,
      },
    },
    rootScrolling: {
      '& #anchor-wrapper': {
        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.04), 0px 1px 8px rgba(0, 0, 0, 0.12)',
        border: '0',
      },
      '& #sticky-header': {
        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.04), 0px 1px 8px rgba(0, 0, 0, 0.12)',
        transition: 'box-shadow 700ms',
      },
    },
    contentWrapper: {
      display: 'flex',
      gap: '4px',
      width: '100%',
    },
    content: {
      position: 'relative',
      overflow: 'hidden',
      flexGrow: 1,
      backgroundColor: 'white',
      borderRadius: '24px 24px 0 0',
    },
    scrollbar: {
      overflow: 'overlay',
      height: `calc(100vh - ${headerHeight})`,
    },
    tabs: {
      position: 'sticky',
      left: '0',
    },
    fixedPanelWrapper: {
      '& .MuiDrawer-paper': {
        top: headerHeight,
        borderRadius: '24px 24px 0 0',
        '& .panel-header': {
          borderRadius: '24px 24px 0 0',
        },
      },
    },
  })
);

interface IProps {}

const LayoutMonitoring = (props: PropsWithChildren<IProps>): JSX.Element => {
  const classes = useStyles();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const { ref, inView } = useInView();

  const { children } = props;

  const [{ title, renderTabs, renderCompactTabs, renderFixedPanel, renderActionButton }] =
    useContext(LayoutWrapperContext);

  const [openNav, setOpenNav] = useState(true);
  const [scrollDirection, setScrollDirection] = useState<'down' | 'up'>();

  useEffect(() => {
    const toggleScrollDirection = (event: any) => {
      if (event.wheelDelta > 0) {
        setScrollDirection('up');
      } else {
        setScrollDirection('down');
      }
    };

    const element = containerRef.current;
    if (element) {
      element.addEventListener('wheel', toggleScrollDirection);
      return () => {
        element.removeEventListener('wheel', toggleScrollDirection);
      };
    }
  });

  useEffect(() => {
    if (inView) {
      setScrollDirection('down');
    }
  }, [inView]);

  return (
    <div className={clsx(classes.root, { [classes.rootScrolling]: !inView })}>
      <Header openNav={openNav} title={title} renderActionButton={renderActionButton} />
      <Box display="flex" width="100%" gridColumnGap="4px">
        <MDSLeftNavBar logo={<MDSMarqContentWhiteLogo />} HomeLink={'/'} style={{ zIndex: 1110 }} onOpenNav={setOpenNav}>
          {RouteParams
            .map((menu) => (
              <MDSNavItem
                key={menu.to}
                label={menu.text}
                link={menu.to}
                icon={<menu.icon />}
              />
            ))}
        </MDSLeftNavBar>
        <main className={clsx(classes.content)}>
          {!inView && scrollDirection === 'up' && renderCompactTabs()}
          <div id="container" ref={containerRef} className={classes.scrollbar}>
            <div ref={ref} className={classes.tabs}>{renderTabs?.()}</div>
            {children}
          </div>
          <ToastContainer />
        </main>
        {renderFixedPanel() && (
          <div id="fixed-panel" className={classes.fixedPanelWrapper}>
            {renderFixedPanel()}
          </div>
        )}
      </Box>
    </div>
  );
};

const Layout = (props: PropsWithChildren<IProps>): JSX.Element => {
  const { children } = props;
  return (
    <MonitoringContextProvider>
      <LayoutMonitoring>
        {children}
      </LayoutMonitoring>
    </MonitoringContextProvider>
  );
};

export default Layout;